// MARK: - Theme Types
export interface ThemeColors {
  // Primary palette
  primary: string;
  secondary: string;
  accent: string;
  
  // Text colors
  text: {
    primary: string;
    secondary: string;
    muted: string;
    error: string;
  };
  
  // Background colors
  background: {
    primary: string;
    secondary: string;
    hover: string;
    active: string;
  };
  
  // Interactive elements
  interactive: {
    button: {
      background: string;
      text: string;
      hover: {
        background: string;
        text: string;
      };
    };
  };
}

export interface ThemeTypography {
  fontFamily: {
    primary: string;
    secondary: string;
  };
  
  fontWeight: {
    normal: string;
    medium: string;
    semibold: string;
    bold: string;
  };
  
  fontSize: {
    xs: string;
    sm: string;
    base: string;
    lg: string;
    xl: string;
    '2xl': string;
  };
  
  letterSpacing: {
    tight: string;
    normal: string;
    wide: string;
    wider: string;
  };
  
  textTransform: {
    uppercase: string;
    lowercase: string;
    capitalize: string;
    none: string;
  };
}

export interface ThemeSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
}

export interface ThemeBorderRadius {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  full: string;
}

export interface ThemeShadows {
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export interface ThemeTransitions {
  fast: string;
  normal: string;
  slow: string;
}

export interface Theme {
  colors: ThemeColors;
  typography: ThemeTypography;
  spacing: ThemeSpacing;
  borderRadius: ThemeBorderRadius;
  shadows: ThemeShadows;
  transitions: ThemeTransitions;
}

// MARK: - Color Definitions
export const colors: ThemeColors = {
  primary: '#FFDABA',
  secondary: '#484825',
  accent: '#E4BC98',
  
  text: {
    primary: '#FFDABA',
    secondary: '#FFDABA99',
    muted: '#FFDABA80',
    error: '#d73444',
  },
  
  background: {
    primary: '#484825',
    secondary: '#484825',
    hover: '#FFFFFF15',
    active: '#484825',
  },
  
  interactive: {
    button: {
      background: '#E4BC98',
      text: '#484825',
      hover: {
        background: 'rgb(228, 194, 162)',
        text: '#484825',
      },
    },
  },
};

// MARK: - Typography Definitions
export const typography: ThemeTypography = {
  fontFamily: {
    primary: '"Neulis Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  
  fontWeight: {
    normal: '480',
    medium: '580',
    semibold: '680',
    bold: '700',
  },
  
  fontSize: {
    xs: '0.8rem',
    sm: '0.9rem',
    base: '1rem',
    lg: '1.2rem',
    xl: '1.5rem',
    '2xl': '1.8rem',
  },
  
  letterSpacing: {
    tight: '0.05em',
    normal: '0.1em',
    wide: '0.2em',
    wider: '0.3em',
  },
  
  textTransform: {
    uppercase: 'uppercase',
    lowercase: 'lowercase',
    capitalize: 'capitalize',
    none: 'none',
  },
};

// MARK: - Spacing Definitions
export const spacing: ThemeSpacing = {
  xs: '0.5rem',
  sm: '0.8rem',
  md: '1rem',
  lg: '1.2rem',
  xl: '1.6rem',
  '2xl': '2rem',
};

// MARK: - Border Radius Definitions
export const borderRadius: ThemeBorderRadius = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '20px',
  full: '50%',
};

// MARK: - Shadow Definitions
export const shadows: ThemeShadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 25px rgba(0, 0, 0, 0.3), 0 5px 10px rgba(0, 0, 0, 0.22)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

// MARK: - Transition Definitions
export const transitions: ThemeTransitions = {
  fast: '0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  normal: '0.22s ease-in',
  slow: '0.6s ease-in-out',
};

// MARK: - Complete Theme
export const theme: Theme = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
};

// MARK: - Theme Export
export default theme; 